
import { Button } from "@/components/ui/button";
import { NewSearchFilter } from "./new-search-filter";
import { useTRPC } from "@/trpc";
import { useAuth } from "@/hooks/use-auth";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { SelectSavedFilterDialog, SavedFilterBase } from "./saved-filter-dialog";
import { useState } from "react";
import { useFormContext } from "react-hook-form";
import { MatchFilterState } from "../(context)/filter";
import RemoveFilter from "@/components/features/filter/remove";
import { toast } from "sonner" 

type FilterField = { field: string; value: string; display: string };
type MatchesSavedFilter = SavedFilterBase & {
  filter?: FilterField[];
};

type FilterProps = {
  onSearch?: () => void;
  onApplyFilters?: (f: MatchesSavedFilter) => void;
  onEditFilter?: (f: MatchesSavedFilter) => void;
  onDeleteFilter?: (f: MatchesSavedFilter) => void;
}

const Filter = ({ onSearch, onApplyFilters, onEditFilter, onDeleteFilter }: FilterProps) => {
  const { trpc } = useTRPC();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const [open, setOpen] = useState(false);

  const { setValue } = useFormContext<MatchFilterState>();

  const [editOpen, setEditOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<MatchesSavedFilter | null>(null);

  const [deleteFilter, setDeleteFilter] = useState<MatchesSavedFilter | null>(null); 

  const { data: filters = [] } = useQuery<MatchesSavedFilter[]>({
    queryKey: ["filter", user?.id, "MATCHES"],
    queryFn: async () =>
      trpc.filters.get.query({ user_id: user!.id, page: "MATCHES" }),
    enabled: !!user?.id
  })

  const applySavedFilterToForm = (f: MatchesSavedFilter) => {
    const items = f.filter ?? [];

    const byField = Object.fromEntries(items.map(i => [i.field, i]));

    const val = (k: string) => (byField[k]?.value as string) ?? "";
    const disp = (k: string) => (byField[k]?.display as string) ?? "";

    setValue("search", val("search"));

    setValue("competition.competition_id",   val("competitie"));
    setValue("competition.competition_name", disp("competitie")); 

    setValue("competition.club_id",   val("club"));
    setValue("competition.club_name", disp("club")); 

    if (val("datum van")) {
      setValue("daterange.date_from", val("datum van"));
    }
    if (val("datum tot")) {
      setValue("daterange.date_to", val("datum tot"));
    }

    setValue("page", 1);
    onSearch?.();
  };

  const startEdit = (f: MatchesSavedFilter) => {
    applySavedFilterToForm(f);
    setEditTarget(f);
    setEditOpen(true);
  };

    const editMutation = useMutation({
      mutationFn: async (data: MatchesSavedFilter) => {
        await trpc.filters.edit.mutate({
          user_id: user!.id,
          filter_id: data.id, 
          filter_name: data.filter_name || "",
          filter: {  
            page: "MATCHES",
            filters: data.filter || [],
          },
        });
      },
      onSuccess: () => {
        setEditOpen(false);
        setEditTarget(null);
        queryClient.invalidateQueries({ queryKey: ["filter", user?.id, "MATCHES"] });
        toast.success("Filter succesvol bijgewerkt");
      },
      onError: (err) => {
        toast.error("Er is een fout opgetreden bij het bijwerken van het filter: ");
      },
    });

  const handleUpdate = ({ values, name }: { values: MatchFilterState, name?: string }) => {
    const updatedFilter: MatchesSavedFilter = {
      ...(editTarget as MatchesSavedFilter),
      filter_name: name || editTarget?.filter_name || "", 
      filter: [
        { field: "search", value: values.search || "", display: values.search || "" },
        { field: "competitie", value: values.competition?.competition_id?.toString() || "", display: values.competition?.competition_name || "" },
        { field: "club", value: values.competition?.club_id?.toString() || "", display: values.competition?.club_name || "" },
        { field: "datum van", value: values.daterange?.date_from?.toString() || "", display: new Date(values.daterange?.date_from || '').toLocaleDateString('nl-BE', { year: 'numeric', month: 'long', day: 'numeric' }) || "" },
        { field: "datum tot", value: values.daterange?.date_to?.toString() || "", display: new Date(values.daterange?.date_to || '').toLocaleDateString('nl-BE', { year: 'numeric', month: 'long', day: 'numeric' }) || "" },
      ].filter((x) => x.value !== ""),
    };
    editMutation.mutate(updatedFilter);
  };

  const handleDelete = (f: MatchesSavedFilter) => {
    setDeleteFilter(f);
  };

  return (
    <div className="p-2 bg-foreground/10 rounded-full flex justify-between items-center">
      <NewSearchFilter 
        onSearch={onSearch}
      />
      <Button variant="link" shape="pill" size="2xl" onClick={() => setOpen(true)}>
        Mijn opgeslagen filters ({filters?.length || 0})
      </Button>

      <NewSearchFilter
        mode="edit"
        open={editOpen}
        onOpenChange={setEditOpen}
        onUpdate={handleUpdate}
        onSearch={onSearch}
        initialFilterName={editTarget?.filter_name}
      />

      <div className="hidden ">
      <SelectSavedFilterDialog<MatchesSavedFilter>
        open={open}
        onOpenChange={setOpen}
        filters={filters}
        onApply={(f) => {
          if (onApplyFilters) onApplyFilters(f);
          else {
            applySavedFilterToForm(f);
            onSearch?.();
          }
          setOpen(false);
        }}
        onEdit={(f) => {
          startEdit(f);
        }}
        onDelete={(f) => {
          handleDelete(f);
        }}
      />
      </div>
        {deleteFilter && (
          <RemoveFilter
            filter_id={deleteFilter.id}
            open={!!deleteFilter}
            onOpenChange={(o) => { if (!o) setDeleteFilter(null); }}
            onRemoved={() => {
              onDeleteFilter?.(deleteFilter);
              setDeleteFilter(null);
              queryClient.invalidateQueries({ queryKey: ["filter", user?.id, "MATCHES"] });
              toast.success("Filter succesvol verwijderd");
            }}
          />
        )}
    </div>
  );
};

export default Filter;
